import { apiDelete, apiGet, apiPost, apiPut } from './client';

export type CameraBroadcastStatus = 'accepted' | 'cancelled' | 'completed' | 'failed' | 'preparing' | 'processing' | 'stopped' | 'streaming' | 'unknown';
export type CameraBroadcastSource = 'DRAFT' | 'LATEST' | 'PRESET';

export interface CameraBroadcastRecording {
  accepted_at: string | null;
  duration_ms: number;
  expires_at: string;
  recording_id: string;
  recorded_at: string;
  sha256: string;
  size_bytes: number;
}

export interface CameraBroadcastDeliveryState {
  attempt_count: number;
  completed_at: string | null;
  error_code: string | null;
  error_message: string | null;
  poll_after_ms: number | null;
  preset_id: string | null;
  request_uid: string;
  requested_at: string;
  source: CameraBroadcastSource;
  stopped_at: string | null;
  status: CameraBroadcastStatus;
  success: boolean | null;
  terminal: boolean;
  updated_at: string;
}

export interface CameraBroadcastPreset {
  disabled_reason: string | null;
  id: string;
  label: string;
  preview_available: boolean;
  send_enabled: boolean;
  source_type: 'JSON' | 'WAV';
}

export interface CameraBroadcastOptions {
  block_reason_code?: string | null;
  media_linked?: boolean | null;
  broadcast_allowed?: boolean | null;
  block_reason: string | null;
  camera_ready: boolean;
  channels: number;
  draft: CameraBroadcastRecording | null;
  enabled: boolean;
  last_delivery: CameraBroadcastDeliveryState | null;
  latest: CameraBroadcastRecording | null;
  max_duration_seconds: number;
  max_file_bytes: number;
  preset_error: string | null;
  presets: CameraBroadcastPreset[];
  sample_rate: number;
  sample_width_bytes: number;
}

export interface CameraBroadcastDeliveryResponse {
  draft: CameraBroadcastRecording | null;
  error_code: string | null;
  latest: CameraBroadcastRecording | null;
  message: string;
  poll_after_ms: number | null;
  request_uid: string;
  status: CameraBroadcastStatus;
  success: boolean | null;
  terminal: boolean;
}

export interface CameraBroadcastReadinessItem {
  camera_uid: string;
  message: string | null;
  reason_code: string | null;
  state: 'available' | 'indeterminate' | 'unavailable';
}

export interface CameraBroadcastReadinessResponse {
  checked_at: string;
  items: CameraBroadcastReadinessItem[];
}

let capabilityPromise: Promise<boolean> | null = null;
let capabilityExpiresAt = 0;

/** 여러 CCTV 위젯이 동시에 렌더링돼도 전역 기능 조회는 짧게 공유합니다. */
export async function fetchCameraBroadcastCapability(): Promise<boolean> {
  if (capabilityPromise && capabilityExpiresAt > Date.now()) {
    return capabilityPromise;
  }

  capabilityExpiresAt = Date.now() + 30_000;
  capabilityPromise = apiGet<{ enabled: boolean }>({ path: '/camera-broadcast/capability' }).then(result => (result.ok ? result.data.enabled === true : false));
  return capabilityPromise;
}

export function fetchCameraBroadcastOptions(cameraUid: string) {
  return apiGet<CameraBroadcastOptions>({
    path: `/camera-broadcast/cameras/${encodeURIComponent(cameraUid)}/options`,
  });
}

export function fetchCameraBroadcastReadiness(cameraUids: string[]) {
  return apiPost<CameraBroadcastReadinessResponse>({
    path: '/camera-broadcast/readiness',
    body: { camera_uids: cameraUids },
  });
}

export function saveCameraBroadcastRecording(cameraUid: string, wav: Blob) {
  const body = new FormData();
  body.append('file', wav, 'camera-broadcast.wav');
  return apiPut<CameraBroadcastOptions>({
    path: `/camera-broadcast/cameras/${encodeURIComponent(cameraUid)}/recording`,
    body,
  });
}

export function deliverCameraBroadcast(
  cameraUid: string,
  input: {
    preset_id?: string;
    source: CameraBroadcastSource;
  }
) {
  return apiPost<CameraBroadcastDeliveryResponse>({
    path: `/camera-broadcast/cameras/${encodeURIComponent(cameraUid)}/deliveries`,
    body: input,
  });
}

export function fetchCameraBroadcastDelivery(cameraUid: string, requestUid: string) {
  return apiGet<CameraBroadcastDeliveryState>({
    path: `/camera-broadcast/cameras/${encodeURIComponent(cameraUid)}/deliveries/${encodeURIComponent(requestUid)}`,
    cache: 'no-store',
  });
}

export function discardCameraBroadcastRecording(cameraUid: string, recordingId: string) {
  return apiDelete<null>({
    path: `/camera-broadcast/cameras/${encodeURIComponent(cameraUid)}/recordings/${encodeURIComponent(recordingId)}`,
  });
}

export function getCameraBroadcastPresetAudioUrl(cameraUid: string, presetId: string) {
  const query = new URLSearchParams({ camera_uid: cameraUid });
  return `/api/proxy/v1/camera-broadcast/presets/${encodeURIComponent(presetId)}/preview?${query.toString()}`;
}
